import React from 'react';
import { AlertTriangle, Loader2, PenLine } from 'lucide-react';
import { ProjectNotice } from '../services/projectLockLogic';

/**
 * Der gelbe Hinweisbalken ueber einem geoeffneten Projekt.
 *
 * Bis hierher stand dasselbe Markup zweimal im Code (App.tsx fuer
 * Vorankuendigung/Unterlage, SiGeKoInspectionModule fuer das Pruefmodul), samt
 * einer zweiten Kopie des Hinweistextes im Modul. Zwei Kopien reichen bereits,
 * damit eine von beiden bei der naechsten Aenderung stehen bleibt — und der
 * Sperrhinweis kommt jetzt als dritter Grund dazu.
 *
 * Die Komponente entscheidet NICHTS. Was drinsteht und ob ein Knopf erscheint,
 * kommt fertig aus `describeProjectNotice` (projectLockLogic.ts) — dort ist es
 * ohne DOM testbar, hier waere es das nicht (kein jsdom im Projekt).
 *
 * Schmal umbrechend: Text und Symbol bilden EIN Element mit Mindestbreite, der
 * Knopf rutscht darunter, sobald beide nicht mehr nebeneinander passen. Ohne
 * diese Klammer quetschte der Knopf den Satz am Handy auf ein Wort je Zeile.
 */
interface Props {
    notice: ProjectNotice;
    /** Uebernahme ausloesen. Fehlt der Handler, erscheint kein Knopf. */
    onTakeover?: () => void;
    /**
     * Begruendungsdialog oeffnen (fremdes Projekt). Fehlt der Handler,
     * erscheint kein Knopf — der Balken nennt dann nur den Grund.
     */
    onJustify?: () => void;
    /** Uebernahme laeuft — Knopf sperren, damit kein Doppelklick zwei RPCs schickt. */
    busy?: boolean;
}

/** Gemeinsame Form beider Knoepfe; die Farbe sagt, was dahinter passiert. */
const KNOPF =
    'ml-auto shrink-0 flex items-center gap-1.5 px-3 py-1 rounded-lg border text-xs font-bold transition-colors ' +
    'disabled:opacity-50 disabled:cursor-not-allowed ';

export const ProjectNoticeBanner: React.FC<Props> = ({ notice, onTakeover, onJustify, busy = false }) => {
    if (notice.kind === 'none') return null;

    const showTakeover = notice.takeover !== 'none' && !!onTakeover;
    const showJustify = notice.kind === 'foreign' && !!onJustify && !!notice.justifyLabel;

    return (
        <div data-tutorial="sperre.banner" className="bg-amber-100 border-b border-amber-300 text-amber-900 px-4 py-2 shrink-0 z-10 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs font-bold">
            <div className="flex items-start gap-2 min-w-0 flex-1 basis-64">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span className="min-w-0">{notice.text}</span>
            </div>
            {showTakeover && (
                <button
                    data-tutorial="sperre.uebernehmen"
                    type="button"
                    onClick={onTakeover}
                    disabled={busy}
                    className={
                        KNOPF +
                        // Das Aufbrechen einer lebenden fremden Sperre ist der
                        // Eingriff und sieht deshalb auch danach aus.
                        (notice.takeover === 'force'
                            ? 'bg-red-600 hover:bg-red-500 border-red-700 text-white'
                            : 'bg-amber-600 hover:bg-amber-500 border-amber-700 text-white')
                    }
                >
                    {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    <span>{notice.takeoverLabel}</span>
                </button>
            )}
            {showJustify && (
                <button
                    data-tutorial="fremdzugriff.begruenden"
                    type="button"
                    onClick={onJustify}
                    disabled={busy}
                    // Kein Rot: hier wird niemandem etwas weggenommen, es wird
                    // nur ein Grund hinterlegt.
                    className={KNOPF + 'bg-amber-600 hover:bg-amber-500 border-amber-700 text-white'}
                >
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PenLine className="w-3.5 h-3.5" />}
                    <span>{notice.justifyLabel}</span>
                </button>
            )}
        </div>
    );
};
